import { useState } from 'react';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';

import { useTheme, Theme } from '@/services/theme';

import { SpriteIcon } from '../SpriteIcon';

const StyledPlus = styled(SpriteIcon)`
  align-self: flex-start;
  border-radius: 50%;
  padding: 10px;
  margin: 0 auto;
`;

const StyledAccordion = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const AccordionItem = styled.div`
  border: 1px solid ${({ theme }: { theme: Theme }) => theme.colors.newGray5};
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${({
    $isOpen,
    $backgroundColor,
  }: {
    $isOpen: boolean;
    $backgroundColor: string;
  }) => ($isOpen ? 'rgba(237, 25, 67, 1) !important' : $backgroundColor)};

  /* &:hover {
    background-color: ${({ theme }: { theme: Theme }) => theme.colors.newGray1};
  } */

  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
  }
`;

const ToggleIcon = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  ${({ $isOpen }: { $isOpen: boolean }) => $isOpen && `transform: rotate(180deg);`}
`;

const ContentWrapper = styled.div`
  overflow: hidden;
`;

const ContentBody = styled.section`
  padding: 16px;
  background-color: ${({ $backgroundColor }: { $backgroundColor: string }) => $backgroundColor};
`;

export type AccordionItemProps = {
  title: React.ReactNode;
  panel: React.ReactNode;
};

export type AccordionProps = {
  defaultActiveIndex?: number;
  items: AccordionItemProps[];
  backgroundColor?: string;
};

export const Accordion = ({ defaultActiveIndex = -1, items, backgroundColor }: AccordionProps) => {
  const { theme } = useTheme();
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);

  const handleToggle = (index: number) => {
    setActiveIndex(activeIndex => (activeIndex === index ? -1 : index));
  };

  return (
    <StyledAccordion>
      {items.map((item, index) => (
        <AccordionItem key={index} theme={theme}>
          <AccordionHeader
            $isOpen={activeIndex === index}
            onClick={() => handleToggle(index)}
            theme={theme}
            $backgroundColor={backgroundColor}
          >
            <h4>{item.title}</h4>
            <ToggleIcon $isOpen={activeIndex === index}>
              <StyledPlus
                color="#fff"
                backgroundColor={theme.colors.newGray5}
                icon="caret-down-alt"
                size={3.2}
                theme={theme}
              />
            </ToggleIcon>
          </AccordionHeader>
          <ContentWrapper>
            <CSSTransition
              in={activeIndex === index}
              timeout={{ enter: 300, appear: 300, exit: 0 }}
              unmountOnExit
            >
              <ContentBody $backgroundColor={backgroundColor}>{item.panel}</ContentBody>
            </CSSTransition>
          </ContentWrapper>
        </AccordionItem>
      ))}
    </StyledAccordion>
  );
};
